import { useMutation } from '@apollo/client/react';
import { useState } from 'react';

import { DataTestIds, DeleteCurrentUserDocument } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';
import { toast } from '@repo/sds/utils/toast';
import { ConfirmationModal } from '@repo/ui/components/elements/ConfirmationModal/ConfirmationModal';
import { SettingsCard } from '@repo/ui/components/elements/settings-card/settings-card';
import { SettingsPageContent } from '@repo/ui/components/elements/settings-page-content/settings-page-content';
import { useCurrentUser } from '@repo/ui/contexts/current-user-context';

export function ManageAccountPage() {
  const { logout, completeLogout } = useCurrentUser();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [deleteUser, { loading: isDeleting }] = useMutation(DeleteCurrentUserDocument, {
    onCompleted: data => {
      if (!data.deleteCurrentUser) {
        toast.error('Account deletion was not confirmed. Please try again.');
        return;
      }
      toast.success('Account deleted successfully');
      void completeLogout();
    },
    onError: error => {
      toast.error(`Failed to delete account: ${error.message}`);
    },
  });

  return (
    <>
      <SettingsPageContent>
        <SettingsCard direction="column">
          <SDSFlexbox direction="column" gap="card-large" fullWidth>
            <SDSFlexbox direction="column" gap="text-medium">
              <SDSText text="Manage Account" type="h5" dataTestId={DataTestIds.ADMIN.SIDEBAR.PAGE_TITLE} />
              <SDSText text="Logout or delete your account" type="body-medium" color="text-secondary" />
            </SDSFlexbox>

            <SDSFlexbox direction="column" gap="no-design-16" fullWidth>
              <SDSButton
                dataTestId={DataTestIds.PROFILE.LOGOUT_BTN}
                isFullWidth
                text="Logout"
                mode="outline"
                status="alert"
                onClick={() => setIsLogoutModalOpen(true)}
                ariaLabel="Logout from your account"
              />
              <SDSButton
                dataTestId={DataTestIds.PROFILE.DELETE_ACCOUNT_BTN}
                isFullWidth
                text="Delete Account"
                mode="outline"
                status="alert"
                isSubmitting={isDeleting}
                onClick={() => setIsDeleteModalOpen(true)}
                ariaLabel="Delete your account"
              />
            </SDSFlexbox>
          </SDSFlexbox>
        </SettingsCard>
      </SettingsPageContent>

      <ConfirmationModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        size="small"
        onConfirm={() => {
          setIsLogoutModalOpen(false);
          logout();
        }}
        title="Are you sure you want to logout from your Account?"
        buttonText="Logout"
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        size="small"
        onConfirm={() => {
          setIsDeleteModalOpen(false);
          void deleteUser();
        }}
        title="Are you sure you want to delete your account? You will be signed out and your account will be permanently deleted after 30 days."
        buttonText="Delete Account"
      />
    </>
  );
}
